import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL, validateToken } from '../services/api';
import {
  SSO_ERROR_MESSAGE,
  SSO_UNAVAILABLE_MESSAGE,
  isSiteflowSsoFragment,
  parseSiteflowSsoFragment,
} from '../services/siteflowSso';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Remove a SiteFlow handoff from the address bar (and so from history) before
// anything else happens, whether or not it turns out to be valid.
const clearUrlFragment = () => {
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, '', `${pathname}${search}`);
};

// Exchange a SiteFlow handoff for a gallery token. Uses fetch rather than the
// axios client so a 401 shows an error here instead of triggering its redirect.
const exchangeSiteflowSsoHandoff = async (handoff) => {
  let response;
  try {
    response = await fetch(`${API_BASE_URL}/auth/siteflow-sso`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(handoff),
    });
  } catch (err) {
    throw new Error(SSO_ERROR_MESSAGE);
  }
  if (!response.ok) {
    throw new Error(response.status === 503 ? SSO_UNAVAILABLE_MESSAGE : SSO_ERROR_MESSAGE);
  }
  const data = await response.json().catch(() => null);
  if (!data?.access_token || !data?.user) {
    throw new Error(SSO_ERROR_MESSAGE);
  }
  return data;
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ssoError, setSsoError] = useState('');
  const initStarted = useRef(false);

  const login = (authData) => {
    setToken(authData.access_token);
    setUser(authData.user);
    setSsoError('');
    localStorage.setItem('access_token', authData.access_token);
    localStorage.setItem('user', JSON.stringify(authData.user));
  };

  useEffect(() => {
    // Run once, even under StrictMode's double effect: the handoff is single-use.
    if (initStarted.current) return;
    initStarted.current = true;

    const completeSiteflowSso = async () => {
      const hash = window.location.hash;
      if (!isSiteflowSsoFragment(hash)) return false;

      clearUrlFragment();
      try {
        const handoff = parseSiteflowSsoFragment(hash);
        if (!handoff) throw new Error(SSO_ERROR_MESSAGE);
        login(await exchangeSiteflowSsoHandoff(handoff));
        navigate('/', { replace: true });
        return true;
      } catch (err) {
        setSsoError(err.message === SSO_UNAVAILABLE_MESSAGE ? SSO_UNAVAILABLE_MESSAGE : SSO_ERROR_MESSAGE);
        navigate('/', { replace: true });
        return false;
      }
    };

    const initializeAuth = async () => {
      if (await completeSiteflowSso()) {
        setLoading(false);
        return;
      }

      // Check for existing token on app load
      const storedToken = localStorage.getItem('access_token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        try {
          // Validate token with backend
          await validateToken();
          // Token is valid, set auth state
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch (error) {
          // Token is invalid/expired, clear storage
          console.log('Token validation failed, clearing auth state');
          localStorage.removeItem('access_token');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  const getAuthHeaders = () => {
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    }
    return {
      'Content-Type': 'application/json',
    };
  };

  const value = {
    user,
    token,
    loading,
    ssoError,
    login,
    logout,
    isAuthenticated,
    getAuthHeaders,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
